import moment from 'moment';
import { isPartnerAdmin, isPetasenseAdmin, isPetasenseViewer } from 'common/rbac/util';
import {
  ALL, DECISIONS, MANAGE_SORTS, NEW_WINDOW_DAYS, NOTE_STATUSES, NOTE_TYPES, READER_QUERY_KEYS, SCREENSHOT_PLACEHOLDER
} from '../constants/releaseNotes.constants';

const lower = value => (value || '').toString().toLowerCase();

// Notes go live against the UTC date (the API's rule), so "today" is UTC too.
export const utcToday = () => moment.utc().format('YYYY-MM-DD');

export const noteMatchesSearch = (note, query) => {
  const q = lower(query).trim();
  if (!q) return true;
  return [note.title, note.summary, note.body, note.area].some(field => lower(field).includes(q));
};

const matchesField = (value, wanted) => !wanted || wanted === ALL || lower(value) === lower(wanted);

/** The reader's filters: type tab, area, release version and free text. */
export const filterReaderNotes = (notes, filters) => notes.filter(note => (
  matchesField(note.type, filters.type)
  && matchesField(note.area, filters.area)
  && matchesField(note.release_version, filters.release)
  && noteMatchesSearch(note, filters.q)
));

const countBy = (items, key, keys) => items.reduce((counts, item) => {
  const value = item[key];
  return value in counts ? { ...counts, [value]: counts[value] + 1 } : counts;
}, keys.reduce((acc, k) => ({ ...acc, [k]: 0 }), { [ALL]: items.length }));

export const countByType = notes => countBy(notes, 'type', Object.values(NOTE_TYPES));

/**
 * The status a note has for readers right now: a scheduled note whose UTC date
 * has come is live, so it counts and shows as published, the API's rule.
 */
export const effectiveStatus = (note, today = utcToday()) => (
  note.status === NOTE_STATUSES.SCHEDULED && note.date <= today ? NOTE_STATUSES.PUBLISHED : note.status
);

/** True when readers can see the note today. */
export const isLive = (note, today = utcToday()) => effectiveStatus(note, today) === NOTE_STATUSES.PUBLISHED;

/** True when `note` went live after the user last read the notes, while any are unread. */
export const isUnread = (note, { count, seenAt }) => !!(
  count > 0 && seenAt && note.live_at && moment.utc(note.live_at).isAfter(moment.utc(seenAt))
);

export const countByStatus = (notes, today = utcToday()) => countBy(
  notes.map(note => ({ status: effectiveStatus(note, today) })), 'status', Object.values(NOTE_STATUSES)
);

/**
 * Notes grouped under their release, in the order the API lists releases
 * (newest first). A release with no matching note is left out.
 */
export const groupNotesByRelease = (notes, releases) => releases
  .map(release => ({
    release,
    notes: notes.filter(note => note.release_id === release.id)
  }))
  .filter(group => group.notes.length > 0);

/**
 * A release's notes in the order a reader meets them: new features as cards,
 * then improvements and fixes as compact lists, each list under its type. A
 * type this page does not know stays a card, so no note is ever dropped.
 */
const LIST_TYPES = [NOTE_TYPES.IMPROVEMENT, NOTE_TYPES.FIX];

export const splitByWeight = notes => ({
  headline: notes.filter(note => LIST_TYPES.indexOf(note.type) === -1),
  lists: LIST_TYPES
    .map(type => ({ type, notes: notes.filter(note => note.type === type) }))
    .filter(list => list.notes.length > 0)
});

/** The newest release overall: it stays Latest whatever the filters hide. */
export const getLatestReleaseId = releases => (releases.length ? releases[0].id : null);

export const isNoteNew = (note, today = utcToday()) => {
  if (!note || !note.date) return false;
  const age = moment.utc(today).diff(moment.utc(note.date), 'days');
  return age >= 0 && age <= NEW_WINDOW_DAYS;
};

/** Distinct areas, case-insensitively, keeping the first spelling met. */
export const getDistinctAreas = (notes) => {
  const seen = {};
  notes.forEach((note) => {
    const area = (note.area || '').trim();
    if (area && !seen[lower(area)]) seen[lower(area)] = area;
  });
  return Object.values(seen).sort((a, b) => a.localeCompare(b));
};

/** The areas with the most notes, for the filter bar's quick presets. */
export const getTopAreas = (notes, limit) => {
  const counts = {};
  notes.forEach((note) => {
    if (note.area) counts[lower(note.area)] = (counts[lower(note.area)] || 0) + 1;
  });
  return getDistinctAreas(notes)
    .sort((a, b) => counts[lower(b)] - counts[lower(a)])
    .slice(0, limit);
};

export const parseReaderQuery = (search) => {
  const params = new URLSearchParams(search || '');
  return READER_QUERY_KEYS.reduce((filters, key) => ({ ...filters, [key]: params.get(key) || '' }), {});
};

export const buildReaderQuery = (filters) => {
  const params = new URLSearchParams();
  READER_QUERY_KEYS.forEach((key) => {
    const value = filters[key];
    // "all" means no filter only for the type tab; in the search box it is a word.
    if (value && !(key === 'type' && value === ALL)) params.set(key, value);
  });
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const filterManageNotes = (notes, filters) => notes.filter(note => (
  matchesField(effectiveStatus(note), filters.status)
  && matchesField(note.type, filters.type)
  && matchesField(note.area, filters.area)
  && matchesField(note.release_version, filters.release)
  && noteMatchesSearch(note, filters.q)
));

const byDateThenId = (a, b) => (a.date === b.date ? a.id - b.id : a.date.localeCompare(b.date));

export const sortManageNotes = (notes, sort) => {
  const sorted = notes.slice();
  if (sort === MANAGE_SORTS.TITLE) return sorted.sort((a, b) => a.title.localeCompare(b.title));
  if (sort === MANAGE_SORTS.TITLE_DESC) return sorted.sort((a, b) => b.title.localeCompare(a.title));
  if (sort === MANAGE_SORTS.OLDEST) return sorted.sort(byDateThenId);
  return sorted.sort((a, b) => byDateThenId(b, a));
};

// A real image needs a host after the scheme: the editor's screenshot snippet
// inserts a bare "https://" as a placeholder until the image exists.

const escapeRegExp = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `text` split into parts, each flagged when it matches the search query. */
export const splitForHighlight = (text, query) => {
  const q = (query || '').trim();
  if (!text || !q) return [{ text: text || '', match: false }];
  return String(text)
    .split(new RegExp(`(${escapeRegExp(q)})`, 'ig'))
    .filter(part => part !== '')
    .map(part => ({ text: part, match: lower(part) === lower(q) }));
};

export const noteLink = (origin, noteId) => `${origin}/release-notes/${noteId}`;

/**
 * Who manages release notes, as UI hints; the API decides every request.
 * Petasense staff manage Petasense's notes (admins write, viewers read), and
 * a partner's admins write their own partner's.
 */
// A partner's admin, and not Petasense staff: they write their partner's notes
// and get the From Petasense tab. Petasense staff always read Petasense's.
export const isPartnerAuthor = user => isPartnerAdmin(user) && !isPetasenseAdmin(user) && !isPetasenseViewer(user);
export const canWriteNotes = user => isPetasenseAdmin(user) || isPartnerAuthor(user);
export const canManageNotes = user => canWriteNotes(user) || isPetasenseViewer(user);

/** The partner's decision on a Petasense note on the From Petasense list. */
export const decisionOf = note => (note && note.decision) || DECISIONS.TO_REVIEW;

export const filterByDecision = (notes, decision) => (
  decision === ALL ? notes : notes.filter(note => decisionOf(note) === decision)
);

export const countByDecision = notes => notes.reduce(
  (counts, note) => ({ ...counts, [decisionOf(note)]: counts[decisionOf(note)] + 1 }),
  {
    [DECISIONS.TO_REVIEW]: 0, [DECISIONS.ADAPTED]: 0, [DECISIONS.SKIPPED]: 0, [ALL]: notes.length
  }
);

/** True while a note's body still holds a screenshot to replace. */
export const hasScreenshotPlaceholder = body => (body || '').indexOf(SCREENSHOT_PLACEHOLDER) !== -1;
